import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_NETSUITE_PATH } from '../../constants.ts';
import { CREATE_NETSUITE_INTEGRATION, GET_NETSUITE_INTEGRATIONS } from '../../queries.ts';
import NetsuiteIntegrationForm, { type NetsuiteFormData } from './NetsuiteIntegrationForm.tsx';

const FORM_ID = 'netsuite-integration-form';

const NetsuiteIntegrationAddPage = () => {
    const { t } = useTranslation('integrations');
    const navigate = useNavigate();
    const { publishToast } = useToaster();

    const backTooltipText = `${t('backTooltipText')} ${t('netsuite.title')}`;

    const [createIntegration] = useMutation(CREATE_NETSUITE_INTEGRATION, {
        refetchQueries: [GET_NETSUITE_INTEGRATIONS],
    });

    const handleCreateIntegration = async (formData: NetsuiteFormData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    clientId: formData.clientId,
                    clientSecret: formData.clientSecret,
                    tokenId: formData.tokenId,
                    tokenSecret: formData.tokenSecret,
                    accountId: formData.accountId,
                    instanceUrl: formData.instanceUrl,
                    restServiceUrl: formData.restServiceUrl,
                    orderFileCabinetFolderId: formData.orderFileCabinetFolderId,
                    orderCustomFormId: formData.orderCustomFormId,
                    orderChannels: formData.orderChannelIds,
                    rfqFileCabinetFolderId: formData.rfqFileCabinetFolderId,
                    rfqCustomFormId: formData.rfqCustomFormId,
                    rfqChannels: formData.rfqChannelIds,
                    universalFileCabinetFolderId: formData.universalFileCabinetFolderId,
                    universalChannels: formData.universalChannelIds,
                    masterDataMode: formData.masterDataMode,
                    invoiceFileCabinetFolderId: formData.invoiceFileCabinetFolderId,
                    invoiceCustomFormId: formData.invoiceCustomFormId,
                    invoiceChannels: formData.invoiceChannelIds,
                    defaultApprovalStatus: formData.defaultApprovalStatus,
                    defaultExpenseAccountId: formData.defaultExpenseAccountId,
                    defaultSubsidiaryId: formData.defaultSubsidiaryId,
                    defaultLocationId: formData.defaultLocationId,
                    useAltaviaMapping: formData.useAltaviaMapping,
                    altaviaRestletUrl: formData.altaviaRestletUrl,
                },
            });
            publishToast({ description: t('netsuite.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_NETSUITE_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('netsuite.createError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('netsuite.title'), to: url(CUSTOMER_INTEGRATION_NETSUITE_PATH) },
                            { label: t('netsuite.form.createTitle') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_NETSUITE_PATH)}>{t('netsuite.form.cancelButton')}</Link>
                        </Button>
                        <Button variant="primary" type="submit" form={FORM_ID}>
                            {t('netsuite.form.createButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <NetsuiteIntegrationForm onSubmit={handleCreateIntegration} formId={FORM_ID} />
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default NetsuiteIntegrationAddPage;
